import { motion } from "framer-motion";
import { CalendarCheck, ShieldCheck, Clock, Sparkles } from "lucide-react";
import { useI18n } from "../i18n";
import { CONTACT } from "../lib/contact";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { trackCalendar, trackWhatsApp } from "../lib/analytics";
import { MortgageSimulator } from "./MortgageSimulator";

const Hero = () => {
  const { t } = useI18n();
  const trustIcons = [ShieldCheck, Clock, Sparkles];

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-20"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0206] via-[#050507] to-[#050507]" />
      <div className="absolute inset-0 gradient-radial" />
      <motion.div
        aria-hidden
        className="absolute -top-32 -left-24 w-[36rem] h-[36rem] rounded-full bg-[#A60056]/20 blur-[120px]"
        animate={{ scale: [1, 1.12, 1], opacity: [0.45, 0.7, 0.45] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        aria-hidden
        className="absolute bottom-0 right-0 w-[30rem] h-[30rem] rounded-full bg-[#AC1361]/15 blur-[120px]"
        animate={{ scale: [1, 1.18, 1], opacity: [0.35, 0.6, 0.35] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut", delay: 1 }}
      />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
        {/* Left */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-7"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-8"
            data-testid="hero-badge"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#A60056] glow-pulse"></span>
            <span className="text-xs tracking-[0.18em] uppercase text-white/80 font-medium">
              {t.hero.badge}
            </span>
          </motion.div>

          <h1
            className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-medium tracking-tight leading-[1.05] text-balance mb-6"
            data-testid="hero-headline"
          >
            <span className="block text-white/90">{t.hero.title_1}</span>
            <span className="block text-gradient-primary mt-2">{t.hero.title_2}</span>
          </h1>

          <p className="text-sm sm:text-base font-medium text-white/95 mb-4 tracking-tight" data-testid="hero-lead">
            {t.hero.lead}
          </p>
          <p
            className="text-base sm:text-lg lg:text-xl text-white/65 max-w-2xl leading-relaxed mb-10"
            data-testid="hero-subtitle"
          >
            {t.hero.subtitle}
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mb-12">
            <a
              href={CONTACT.calendar}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackCalendar("hero")}
              className="btn-primary"
              data-testid="hero-cta-primary"
            >
              <CalendarCheck className="w-4 h-4" />
              {t.hero.cta_primary}
            </a>
            <a
              href={CONTACT.whatsapp()}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackWhatsApp("hero")}
              className="btn-whatsapp"
              data-testid="hero-cta-whatsapp"
            >
              <WhatsAppIcon className="w-4 h-4" />
              {t.hero.cta_whatsapp}
            </a>
          </div>

          <div className="flex flex-wrap gap-x-8 gap-y-4">
            {t.hero.trust.map((tx, i) => {
              const Icon = trustIcons[i];
              return (
                <div key={tx} className="flex items-center gap-2 text-sm text-white/60" data-testid={`hero-trust-${i}`}>
                  <Icon className="w-4 h-4 text-[#A60056]" />
                  {tx}
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Right — Interactive Simulator */}
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5 relative flex justify-center lg:justify-end min-w-0 w-full"
          data-testid="hero-portrait"
        >
          <MortgageSimulator />
        </motion.div>
      </div>

      <div className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-t from-[#050507] to-transparent pointer-events-none" />
    </section>
  );
};

export default Hero;
